import { GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { QuoteTypeStore } from '../quoteTypeStore';
import { PreflightStep } from '../../utils/constants';
import { getPreflightSteps } from '../../utils/utils';
import { formatCurrency } from '@roc/ui/utils';
import { insertIf, isNil, LoanSubType } from '@roc/feature-utils';
import { SecondAppraisalFormStore } from './secondAppraisalFormStore';
import { AppraisalQuestionnaireV2FormStore } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/appraisalQuestonnaireV2FormStore';

export class PreflightStore {
  private globalStore: GlobalStore;
  private quoteTypeStore: QuoteTypeStore;
  public secondAppraisalFormStore: SecondAppraisalFormStore;
  public appraisalQuestionnaireFormStore: AppraisalQuestionnaireV2FormStore;

  step: PreflightStep;
  nextStep: PreflightStep;
  itemIndex?: number;

  completedBorrowers: string[];
  completedProperties: string[];

  acknowledgeTerms: boolean;

  constructor(globalStore: GlobalStore, quoteTypeStore: QuoteTypeStore) {
    this.globalStore = globalStore;
    this.quoteTypeStore = quoteTypeStore;
    this.secondAppraisalFormStore = new SecondAppraisalFormStore(this.globalStore)
    this.appraisalQuestionnaireFormStore = new AppraisalQuestionnaireV2FormStore(this.globalStore, null);
    makeObservable(this, {
      reset: action,
      step: observable,
      nextStep: observable,
      itemIndex: observable,
      acknowledgeTerms: observable,
      completedBorrowers: observable,
      completedProperties: observable,
      toggleAcknowledgeTerms: action,
      validateCurrentStep: action,
      getNextItemIndex: action,
      goToStep: flow,
      goToNextStep: flow,
      loadPreflightState: action,
      addBorrower: action,
      resetPreflightSteps: action,
      validateRentalProperties: action,
      validateAllSteps: action,
    });
  }

  *goToStep(step: PreflightStep, index?: number) {
    const preflightSteps = this.getPreflightSteps();
    if (preflightSteps.indexOf(step) < preflightSteps.indexOf(this.step)) {
      this.step = step;
      this.itemIndex = index ?? 0;
    } else if (this.validateCurrentStep()) {
      this.itemIndex = index ?? 0;
      this.step = step;
      this.updateNextStep();
      if (!this.quoteTypeStore.isEditingLoan) {
        yield this.quoteTypeStore.saveQuote();
      }
    }
  }

  *goToNextStep() {
    if (this.validateCurrentStep()) {
      const nextItemIndex = this.getNextItemIndex();
      if (nextItemIndex !== null) {
        yield this.goToStep(this.step, nextItemIndex);
      } else {
        const preflightSteps = this.getPreflightSteps();
        const stepIndex = preflightSteps.indexOf(this.step);
        const nextStep = preflightSteps?.[stepIndex + 1];
        yield this.goToStep(nextStep);
      }
    }
  }

  getNextItemIndex() {
    if (this.step === PreflightStep.BORROWER) {
      const borrowers = this.quoteTypeStore.borrowersStore.borrowerFormStores;
      return borrowers?.[this.itemIndex + 1] ? this.itemIndex + 1 : null;
    } else if (this.step === PreflightStep.PROPERTY) {
      const properties = this.quoteTypeStore.propertiesStore.propertyFormStores;
      return properties?.[this.itemIndex + 1] ? this.itemIndex + 1 : null;
    }
    return null;
  }

  private updateNextStep() {
    const preflightSteps = this.getPreflightSteps();
    if (
      preflightSteps.indexOf(this.nextStep) < preflightSteps.indexOf(this.step)
    ) {
      this.nextStep = this.step;
    }
  }

  private getPreflightSteps() {
    const allowLoanPricing = this.quoteTypeStore.userStore.allowLoanPricing;
    const hasBroker = this.quoteTypeStore.brokerStore.brokerFormStore.form.fields.hasBroker.value && !this.globalStore?.userFeatures?.isBroker;
    return [
      PreflightStep.BORROWER,
      ...insertIf(hasBroker, [PreflightStep.BROKER]),
      PreflightStep.PROPERTY,
      ...insertIf(allowLoanPricing, [PreflightStep.CLOSING]),
      PreflightStep.APPRAISAL,
      PreflightStep.SUBMISSION,
    ];
  }

  loadPreflightState(quoteData) {
    const uiState = quoteData?.uiState;
    if (uiState) {
      this.step = uiState.preflightStep;
      this.nextStep = uiState.preflightNextStep;
      this.completedBorrowers = uiState.completedBorrowers || [];
      this.completedProperties = uiState.completedProperties || [];
    }
    if (!isNil(quoteData?.closingData?.valuationQuestionnaire)) {
      this.appraisalQuestionnaireFormStore.loadForm(quoteData?.closingData?.valuationQuestionnaire);
    }
  }

  getPrefligthState() {
    return {
      preflightStep: this.step,
      preflightNextStep: this.nextStep,
      completedBorrowers: this.completedBorrowers,
      completedProperties: this.completedProperties,
    };
  }

  validateCurrentStep() {
    const borrowerFormStore = this.quoteTypeStore.borrowersStore
      .borrowerFormStores?.[this.itemIndex];
    const propertyFormStore = this.quoteTypeStore.propertiesStore
      .propertyFormStores?.[this.itemIndex];

    switch (this.step) {
      case PreflightStep.BROKER:
        if (!this.globalStore?.userFeatures?.isBroker) {
          return this.quoteTypeStore.brokerStore.validateBroker_preflight();
        } else {
          return true;
        }
      case PreflightStep.ENTITY:
        return this.quoteTypeStore.entityStore.validateEntity();
      case PreflightStep.BORROWER:
        if (borrowerFormStore) {
          this.completedBorrowers = this.completedBorrowers.concat(
            borrowerFormStore.key
          );
          return borrowerFormStore.validateBorrower(true);
        } else {
          return true;
        }
      case PreflightStep.PROPERTY:
        this.completedProperties = this.completedProperties.concat(
          propertyFormStore.key
        );
        let extraPropertyValidations = propertyFormStore.validateAndCleanupProperty();
        return (propertyFormStore.validateProperty(true) && extraPropertyValidations);
      case PreflightStep.CLOSING:
        return this.quoteTypeStore.closingStore.validateClosingData();
      default:
        return true;
    }
  }

  validateAllSteps() {

    return (
      this.validateEntity() &&
      this.validateAllBorrowers() &&
      this.validateBroker() &&
      this.validateAllProperties() &&
      this.validateClosing() &&
      this.validateValuation()
    );
  }

  private validateValuation() {
    if (!this.quoteTypeStore.showValuationCard) return true;

    const properties = this.quoteTypeStore.propertiesStore.getProperties().map(p => ({
      propertyId: p.propertyId,
      propertyAddress: p.address,
      address: p.address,
      uuid: p.uuid,
    }));
    if (!this.appraisalQuestionnaireFormStore.validateQuestionnaire(properties)) {
      this.goToStep(PreflightStep.APPRAISAL);
      return false;
    }
    return true;
  }

  private validateEntity() {
    if (!this.quoteTypeStore.entityStore.validateEntity()) {
      this.goToStep(PreflightStep.ENTITY);
      return false;
    }
    return true;
  }

  private validateAllBorrowers() {
    return this.quoteTypeStore.borrowersStore.borrowerFormStores?.every((borrStore, borrIndex) => {
      if (!borrStore.validateBorrower(true)) {
        this.goToStep(PreflightStep.BORROWER, borrIndex);
        return false;
      }
      return true;
    });
  }

  private validateBroker() {
    const hasBroker = this.quoteTypeStore.brokerStore.brokerFormStore.form.fields.hasBroker.value && !this.globalStore?.userFeatures?.isBroker;

    if (hasBroker && !this.globalStore?.userFeatures?.isBroker) {
      if (!this.quoteTypeStore.brokerStore.validateBroker_preflight()) {
        this.goToStep(PreflightStep.BROKER);
        return false;
      }
    }

    return true;
  }

  private validateAllProperties() {
    return this.quoteTypeStore.propertiesStore.propertyFormStores?.every((propStore, propIndex) => {
      if (!propStore.validateProperty(true) || !propStore.validateAndCleanupProperty()) {
        this.goToStep(PreflightStep.PROPERTY, propIndex);
        return false;
      }
      return true;
    });
  }

  private validateClosing() {
    const allowLoanPricing = this.quoteTypeStore.userStore.allowLoanPricing;

    if (allowLoanPricing) {
      if (!this.quoteTypeStore.closingStore.validateClosingData()) {
        this.goToStep(PreflightStep.CLOSING);
        return false;
      }
    }

    return true;
  }

  isStepComplete(step: PreflightStep, itemIndex?: number) {
    const preflightSteps = this.getPreflightSteps();
    if (preflightSteps.indexOf(step) <= preflightSteps.indexOf(this.nextStep)) {
      if (step === PreflightStep.BORROWER) {
        const key = this.quoteTypeStore.borrowersStore.borrowerFormStores[
          itemIndex
        ].key;
        return this.completedBorrowers.includes(key);
      } else if (step === PreflightStep.PROPERTY) {
        const key = this.quoteTypeStore.propertiesStore.propertyFormStores[
          itemIndex
        ].key;
        return this.completedProperties.includes(key);
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  addBorrower() {
    if (this.validateCurrentStep()) {
      this.quoteTypeStore.borrowersStore.addNewBorrower();
      const borrowerIndex =
        this.quoteTypeStore.borrowersStore.borrowerFormStores.length - 1;
      this.goToStep(PreflightStep.BORROWER, borrowerIndex);
    }
  }

  reset() {
    const steps = this.getPreflightSteps();
    this.appraisalQuestionnaireFormStore.reset();
    this.step = steps[0];
    this.nextStep = steps[0];
    this.acknowledgeTerms = false;
    this.itemIndex = 0;
    this.completedBorrowers = [];
    this.completedProperties = [];
  }

  toggleAcknowledgeTerms() {
    this.acknowledgeTerms = !this.acknowledgeTerms;
  }

  resetPreflightSteps() {
    const steps = this.getPreflightSteps();
    this.step = steps[0];
    this.nextStep = steps[0];
  }

  validateRentalProperties(isLastProperty: boolean): boolean {
    const acquisitionPrice = this.quoteTypeStore.portfolioInformationFormStore.getFormValues().acquisitionPrice;
    const estimatedAsIsValue = this.quoteTypeStore.portfolioInformationFormStore.totalEstimatedAsIsValue;

    if (isLastProperty) {

      const properties = this.quoteTypeStore.propertiesStore.getProperties();

      const totalPurchasePrice = properties.reduce((sum, p) => sum + (Number(p.purchasePrice) || 0), 0);
      const totalAsIsValue = properties.reduce((sum, p) => sum + (Number(p.propertyValuation) || 0), 0);

      const matchesPurchasePrice = acquisitionPrice === totalPurchasePrice;
      const matchesAsIsValue = estimatedAsIsValue === totalAsIsValue;

      if (!matchesPurchasePrice) {
        this.globalStore.notificationStore.showErrorNotification({
          message: `Total purchase price: ${formatCurrency(totalPurchasePrice)} should match previously entered value: ${formatCurrency(acquisitionPrice)}`,
        });
      }

      if (!matchesAsIsValue) {
        this.globalStore.notificationStore.showErrorNotification({
          message: `Total As Is Value: ${formatCurrency(totalAsIsValue)} should match previously entered value: ${formatCurrency(estimatedAsIsValue)}`,
        });
      }

      return matchesPurchasePrice && matchesAsIsValue;
    } else {

      const properties = this.quoteTypeStore.propertiesStore.getProperties().slice(0, this.itemIndex + 1);

      const totalPurchasePrice = properties.reduce((sum, p) => sum + (Number(p.purchasePrice) || 0), 0);
      const totalAsIsValue = properties.reduce((sum, p) => sum + (Number(p.propertyValuation) || 0), 0);

      const isPurchasePriceValid = acquisitionPrice > totalPurchasePrice;
      const isAsIsValueValid = estimatedAsIsValue > totalAsIsValue;

      if (!isPurchasePriceValid) {
        this.globalStore.notificationStore.showErrorNotification({
          message: `Total purchase price: ${formatCurrency(totalPurchasePrice)} should not be greater than previously entered value: ${formatCurrency(acquisitionPrice)}`,
        });
      }

      if (!isAsIsValueValid) {
        this.globalStore.notificationStore.showErrorNotification({
          message: `Total As Is Value: ${formatCurrency(totalAsIsValue)} should not be greater than previously entered value: ${formatCurrency(estimatedAsIsValue)}`,
        });
      }

      return isPurchasePriceValid && isAsIsValueValid;
    }
  }
}
